import type { ReactNode } from 'react';

export function Card({ children, className = '' }: { children: ReactNode; className?: string }) {
  return <div className={`card-base p-6 ${className}`}>{children}</div>;
}

export function StatCard({
  label,
  value,
  hint,
  accent,
}: {
  label: string;
  value: ReactNode;
  hint?: string;
  accent?: 'magenta' | 'violet' | 'azure' | 'ink';
}) {
  const bar =
    accent === 'magenta'
      ? 'bg-magenta-500'
      : accent === 'azure'
        ? 'bg-azure-500'
        : accent === 'ink'
          ? 'bg-ink'
          : 'bg-violet-500';
  return (
    <div className="card-base p-5 relative overflow-hidden">
      <div className={`absolute left-0 top-0 bottom-0 w-1 ${bar}`} />
      <div className="eyebrow mb-2">{label}</div>
      <div className="stat-num text-ink">{value}</div>
      {hint && <div className="text-xs text-ink-600 mt-1">{hint}</div>}
    </div>
  );
}

type BadgeKind = 'success' | 'warn' | 'danger' | 'info' | 'neutral';
const badgeStyles: Record<BadgeKind, string> = {
  success: 'bg-success/10 text-success border-success/20',
  warn: 'bg-warn/10 text-warn border-warn/30',
  danger: 'bg-danger/10 text-danger border-danger/20',
  info: 'bg-azure-500/10 text-azure-600 border-azure-500/20',
  neutral: 'bg-paper-200 text-ink-700 border-paper-300',
};

export function Badge({ kind = 'neutral', children }: { kind?: BadgeKind; children: ReactNode }) {
  return (
    <span
      className={`inline-flex items-center px-2.5 py-0.5 text-xs font-mono uppercase tracking-wider rounded-full border ${badgeStyles[kind]}`}
    >
      {children}
    </span>
  );
}

export function Spinner({ size = 24 }: { size?: number }) {
  return (
    <span
      style={{ width: size, height: size }}
      className="inline-block rounded-full border-2 border-violet-500 border-r-transparent animate-spin"
    />
  );
}

export function Empty({ title, hint }: { title: string; hint?: string }) {
  return (
    <div className="card-base p-10 text-center">
      <div className="text-ink-600 font-display text-lg">{title}</div>
      {hint && <div className="text-sm text-ink-600 mt-1">{hint}</div>}
    </div>
  );
}
